import { useCommonTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

interface LoadingSpinnerProps {
  /**
   * Size variant of the spinner
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Additional CSS classes to apply to the spinner container
   */
  className?: string;
  /**
   * Optional text to display below the spinner
   */
  text?: string;
  /**
   * Whether to center the spinner in its container
   */
  centered?: boolean;
  /**
   * Color variant of the spinner
   */
  variant?: 'primary' | 'secondary' | 'muted';
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
};

const variantClasses = {
  primary: '!border-primary',
  secondary: '!border-gray-400',
  muted: '!border-muted-foreground',
};

/**
 * LoadingSpinner component provides a consistent loading indicator
 * across the application with various size and styling options.
 */
export default function LoadingSpinner({
  size = 'md',
  className,
  text,
  centered = false,
  variant = 'secondary',
}: LoadingSpinnerProps) {
  const { t } = useCommonTranslation();

  const spinnerElement = (
    <div
      className={cn(
        'animate-spin rounded-full border-b-3',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      role='status'
      aria-label={text || t('status.loading', 'Loading...')}
    />
  );

  const content = (
    <div
      className={cn(
        'flex flex-col items-center gap-2',
        centered && 'justify-center'
      )}
    >
      {spinnerElement}
      {text && (
        <p className='text-sm text-muted-foreground' aria-live='polite'>
          {text}
        </p>
      )}
    </div>
  );

  if (centered) {
    return (
      <div className='flex items-center justify-center min-h-32'>{content}</div>
    );
  }

  return content;
}

// Export the props type for reuse
export type { LoadingSpinnerProps };
